import { useRef, useState, useCallback } from 'react';
import { Pose } from '@mediapipe/pose';
import { Camera } from '@mediapipe/camera_utils';
import { usePoseContext } from '../PoseContext';

export default function usePoseLoader(onResultsCallback) {
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [statusText, setStatusText] = useState('Iniciando...');
  const poseRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const callbackRef = useRef(onResultsCallback);
  const stoppedRef = useRef(false);
  const ownsInstance = useRef(false);
  callbackRef.current = onResultsCallback;

  const poseContext = usePoseContext();

  const start = useCallback((video) => {
    if (!video) return;
    videoRef.current = video;

    // Intentar usar instancia pre-cargada del contexto
    const preloadedPose = poseContext?.getPose();

    if (preloadedPose && poseContext.ready) {
      // Pose ya está cargado - solo conectar cámara
      console.log('[usePoseLoader] Usando Pose pre-cargado del contexto');
      setStatusText('Modelo listo, activando cámara...');
      setProgress(80);

      poseRef.current = preloadedPose;
      ownsInstance.current = false;

      // Reasignar el callback de resultados
      let firstResult = false;
      preloadedPose.onResults((results) => {
        if (!firstResult) {
          firstResult = true;
          setProgress(100);
          setStatusText('¡Listo!');
          setTimeout(() => setLoaded(true), 200);
        }
        if (callbackRef.current) callbackRef.current(results);
      });

      // Iniciar cámara
      stoppedRef.current = false;
      const camera = new Camera(video, {
        onFrame: async () => {
          if (videoRef.current && poseRef.current && !stoppedRef.current) {
            try { await poseRef.current.send({ image: videoRef.current }); } catch(e) {}
          }
        },
        width: 640,
        height: 480
      });

      cameraRef.current = camera;
      camera.start().then(() => {
        setStatusText('Procesando primera imagen...');
        setProgress(90);
      });

    } else {
      // Fallback: crear instancia propia (primera carga o contexto no disponible)
      console.log('[usePoseLoader] Creando instancia propia de Pose (fallback)');
      setStatusText('Cargando modelo de movimiento...');
      setProgress(10);

      const pose = new Pose({
        locateFile: (file) => {
          if (file.endsWith('.tflite') || file.endsWith('.binarypb')) {
            setStatusText('Descargando modelo IA...');
            setProgress(30);
          } else if (file.endsWith('.wasm')) {
            setStatusText('Cargando motor de detección...');
            setProgress(20);
          }
          return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
        }
      });

      pose.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      let firstResult = false;
      pose.onResults((results) => {
        if (!firstResult) {
          firstResult = true;
          setProgress(100);
          setStatusText('¡Listo!');
          setTimeout(() => setLoaded(true), 400);
        }
        if (callbackRef.current) callbackRef.current(results);
      });

      poseRef.current = pose;
      ownsInstance.current = true;
      setStatusText('Activando cámara...');
      setProgress(50);

      stoppedRef.current = false;
      const camera = new Camera(video, {
        onFrame: async () => {
          if (videoRef.current && poseRef.current && !stoppedRef.current) {
            try { await poseRef.current.send({ image: videoRef.current }); } catch(e) {}
          }
        },
        width: 640,
        height: 480
      });

      cameraRef.current = camera;
      camera.start().then(() => {
        setStatusText('Procesando primera imagen...');
        setProgress(70);
      });
    }
  }, [poseContext]);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (cameraRef.current) { try { cameraRef.current.stop(); } catch(e) {} cameraRef.current = null; }
    // Solo cerrar pose si es instancia propia (no la compartida del contexto)
    if (ownsInstance.current && poseRef.current) {
      try { poseRef.current.close(); } catch(e) {}
    }
    poseRef.current = null;
  }, []);

  return { progress, loaded, statusText, start, stop, poseRef, cameraRef };
}
